import React, { useEffect, useState, useMemo, useCallback, FC } from "react";
import { ALIGN, COLOR, Box, Layout, Panel, ScrollView, View, useDevice, InfoBox } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button } from "@lookiero/sty-psp-ui";
import { Order, ProductWithFeedback } from "../../../../../../domain/entities/order/Order";
import { Product } from "../../../../../../domain/entities/product/Product";
import { useGetReturningOrderPaymentBreakdown } from "../../../../behaviors/useGetReturningOrderPaymentBreakdown";
import { ProductList } from "../../../../components/productList/ProductList";
import { useFormatCurrency } from "../../../../hooks/useFormatCurrency";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { financial } from "../../../../shared/util/financial/financial";
import { PanelHeader } from "../../header/panelHeader/PanelHeader";
import { TotalTable } from "../totalTable/TotalTable";
import { style as returnPanelStyle } from "./ReturnPanel.style";

interface ReturnPanelProps {
  readonly currentOrder: Order;
  readonly selectedItems: ProductWithFeedback[];
  readonly onBack: () => void;
  readonly onNext: () => void;
  readonly setSelectedItems: (items: ProductWithFeedback[]) => void; // we're using this callback to mutate the state. It should an onSelect callback
}

const ReturnPanel: FC<ReturnPanelProps> = ({ currentOrder, selectedItems, onBack, onNext, setSelectedItems }) => {
  const { screen } = useDevice();
  const isDesktopScreen = screen.L;

  const { checkoutId, currency } = currentOrder;
  const [totalPrice, setTotalPrice] = useState(currentOrder.totalPrice as number);

  const style = useMemo(() => returnPanelStyle(), []);

  /**
   * TODO Remove this use case when we could split this component in a isolated URL -> View and template.
   *
   * This issue will be faced in https://lookiero.atlassian.net/browse/LK-31291
   */
  const breakdown = useGetReturningOrderPaymentBreakdown(checkoutId, totalPrice, currency);

  const formatCurrency = useFormatCurrency();
  const formatBreakdownPaymentAmount = formatCurrency({ amount: financial(breakdown.payment.amount) });
  const formatBreakdownBalanceAmount = formatCurrency({ amount: financial(breakdown.balance.amount) });

  const orderReturnIntroText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_RETURN_INTRO,
  });
  const orderReturnCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_RETURN_CTA,
  });
  const returnInformationAboutBalanceText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURN_INFORMATION_ABOUT_BALANCE,
    values: {
      ReturnAmountPaymentMethod: formatBreakdownPaymentAmount,
      ReturnAmountBalanceMethod: formatBreakdownBalanceAmount,
    },
  });

  const discardedItemsPrice = useMemo(() => {
    if (!selectedItems) return 0;
    return selectedItems.reduce((acc, { price, isProductWithDiscount }) => {
      const amount = isProductWithDiscount && price.discountedPrice ? price.discountedPrice.amount : price.amount;
      return acc + amount;
    }, 0);
  }, [selectedItems]);

  useEffect(() => {
    const orderTotalPrice = currentOrder.totalPrice || 0;
    const total = orderTotalPrice - discardedItemsPrice;
    setTotalPrice(total);
  }, [currentOrder.totalPrice, discardedItemsPrice]);

  const items = currentOrder.items
    ? currentOrder.items.sort((productA: Product, productB: Product) =>
        productA.variantId > productB.variantId ? 1 : -1,
      )
    : [];
  const itemsCount = items.length;

  const handleSelectItem = useCallback(
    // oxlint-disable-next-line typescript/no-explicit-any
    (product: any, selected: boolean) => {
      if (selected) {
        setSelectedItems([...selectedItems, product]);
      } else {
        setSelectedItems(selectedItems.filter((item) => item.itemId !== product.itemId));
      }
    },
    [selectedItems, setSelectedItems],
  );

  const returnedTotalPrice = useMemo(() => {
    const returnedItemsPrice = currentOrder.returnedItemsPrice || 0;
    const pendingReturnItemsPrice = currentOrder.pendingReturnItemsPrice || 0;
    return returnedItemsPrice + pendingReturnItemsPrice;
  }, [currentOrder]);

  return (
    <Panel
      align={ALIGN.RIGHT}
      alignItems={ALIGN.CENTER}
      color={screen.L ? COLOR.BG_PRIMARY_LIGHT : COLOR.BG_BASE}
      isVisible
      onClose={() => void 0}
    >
      <PanelHeader onBack={onBack} />

      <ScrollView>
        <Layout fullWidth={!isDesktopScreen} style={isDesktopScreen ? style.layout : undefined}>
          {items && items.length && (
            <>
              <Box
                size={{ L: "2/3" }}
                style={isDesktopScreen ? style.detailWrapperLarge : undefined}
                testID="return-panel"
              >
                <View style={isDesktopScreen ? style.detailLarge : style.detailSmall}>
                  <View style={style.infoBox}>
                    <InfoBox icon="info" text={orderReturnIntroText} />
                  </View>
                  <ProductList
                    products={items}
                    selectedItems={selectedItems}
                    onPressCard={handleSelectItem}
                    onSelect={handleSelectItem}
                  />
                </View>
              </Box>

              <Box
                size={{ L: "1/3" }}
                style={[style.resumeWrapper, isDesktopScreen ? style.resumeWrapperLarge : undefined]}
              >
                <View>
                  {currentOrder && (
                    <TotalTable
                      discardedItemsPrice={discardedItemsPrice}
                      initialPrice={currentOrder.initialPrice || 0}
                      itemsCount={itemsCount}
                      promo={currentOrder.promo}
                      returnedTotalPrice={returnedTotalPrice}
                      totalPrice={totalPrice || 0}
                      unavailableItemsPrice={currentOrder.discardedItemsPrice || 0}
                    />
                  )}
                  <View style={style.returnButton}>
                    <Button
                      disabled={!selectedItems.length}
                      style={{ button: style.fullWidthButton }}
                      testID="button-return-panel-next"
                      onPress={onNext}
                    >
                      {orderReturnCtaText}
                    </Button>
                  </View>

                  {selectedItems.length > 0 && breakdown.balance.amount > 0 && (
                    <InfoBox style={style.balanceBanner} text={returnInformationAboutBalanceText} />
                  )}
                </View>
              </Box>
            </>
          )}
        </Layout>
      </ScrollView>
    </Panel>
  );
};

export { ReturnPanel };
